import { Link } from "react-router-dom";
import {
  Linkedin,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  MapPin,
  Phone,
  Mail,
} from "lucide-react";
import { CONTACT, NAV_LINKS } from "@/lib/constants";
import { useSettings } from "@/context/SettingsContext";

export default function Footer() {
  const year = new Date().getFullYear();
  const s = useSettings();
  const socials = [
    { url: s.social_linkedin, icon: Linkedin, key: "linkedin" },
    { url: s.social_facebook, icon: Facebook, key: "facebook" },
    { url: s.social_instagram, icon: Instagram, key: "instagram" },
    { url: s.social_twitter, icon: Twitter, key: "twitter" },
    { url: s.social_youtube, icon: Youtube, key: "youtube" },
  ].filter((x) => x.url);
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0B2545] text-slate-200 border-t-4 border-[#2A9D8F]"
    >
      <div className="container-fr px-6 md:px-12 lg:px-20 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* Brand */}
        <div>
          <Link to="/" className="flex items-center gap-3 mb-5">
            <div className="h-14 w-14 rounded-md bg-white p-1 flex items-center justify-center">
              <img src="/forage-logo.jpg" alt="Forage logo" className="h-full w-full object-contain" />
            </div>
            <div className="leading-tight">
              <div className="font-display text-white text-xl font-bold">Forage</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                Animal Health & Nutrition
              </div>
            </div>
          </Link>
          <p className="text-sm text-slate-300 leading-relaxed">
            {CONTACT.tagline}. Pharmaceutical-grade veterinary supplements and
            nutrition manufactured in India for distributors, veterinarians,
            exporters and institutional buyers worldwide.
          </p>
          <div className="flex gap-3 mt-5">
            {socials.length === 0 ? (
              <span className="text-xs text-slate-400 italic">Social links coming soon</span>
            ) : (
              socials.map((s) => (
                <a
                  key={s.key}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`social-${s.key}`}
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#2A9D8F] flex items-center justify-center transition-colors"
                >
                  <s.icon size={16} />
                </a>
              ))
            )}
          </div>
        </div>

        {/* Quick links */}
        <div>
          <h4 className="font-display text-white text-lg font-semibold mb-5">
            Explore
          </h4>
          <ul className="space-y-2">
            {NAV_LINKS.slice(0, 6).map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-sm text-slate-300 hover:text-white transition-colors"
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display text-white text-lg font-semibold mb-5">
            Company
          </h4>
          <ul className="space-y-2">
            {NAV_LINKS.slice(6).map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-sm text-slate-300 hover:text-white transition-colors"
                  data-testid={`footer-link2-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </Link>
              </li>
            ))}

          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="font-display text-white text-lg font-semibold mb-5">
            Reach Us
          </h4>
          <ul className="space-y-3 text-sm text-slate-300">
            <li className="flex gap-3">
              <MapPin size={16} className="text-[#2A9D8F] flex-shrink-0 mt-1" />
              <span>
                {CONTACT.address.line1},<br />
                {CONTACT.address.line2}, {CONTACT.address.state} -{" "}
                {CONTACT.address.pincode}, {CONTACT.address.country}
              </span>
            </li>
            <li className="flex gap-3">
              <Phone size={16} className="text-[#2A9D8F] flex-shrink-0 mt-1" />
              <a href={`tel:${CONTACT.phoneTel}`} className="hover:text-white">
                {CONTACT.phone}
              </a>
            </li>
            <li className="flex gap-3">
              <Mail size={16} className="text-[#2A9D8F] flex-shrink-0 mt-1" />
              <a href={`mailto:${CONTACT.email}`} className="hover:text-white">
                {CONTACT.email}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-fr px-6 md:px-12 py-5 flex flex-col md:flex-row justify-between gap-3 text-xs text-slate-400">
          <p>
            © {year} {CONTACT.company}. All rights reserved.
          </p>
          <p>
            Manufactured in India • For Veterinary Use • Pharmaceutical Grade
          </p>
        </div>
      </div>
    </footer>
  );
}
